import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, LogOut, Pencil, Plus, RefreshCw, ShieldCheck, Trash2 } from "lucide-react";
import { Toaster, toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["New", "Contacted", "Closed"];
const emptyProject = { name: "", client: "", location: "", year: "", description: "", photo: "" };

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  return String(detail);
}

const authHeaders = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

function ResetPasswordView({ resetToken }) {
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (event) => {
    event.preventDefault();
    setError("");
    const data = Object.fromEntries(new FormData(event.currentTarget));
    if (data.password !== data.confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token: resetToken, password: data.password });
      setDone(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  return (
    <div className="admin-card" data-testid="reset-password-page">
      <a href="/admin" className="admin-back" data-testid="reset-back-link"><ArrowLeft size={16} /> Back to sign in</a>
      <h1>Set a new password</h1>
      {done ? (
        <p data-testid="reset-success-message">Password updated. <a href="/admin" data-testid="reset-success-login-link">Sign in with your new password</a>.</p>
      ) : (
        <form onSubmit={submit} data-testid="reset-password-form">
          <p>Choose a new password for your admin account.</p>
          <label>New password<input type="password" name="password" required minLength={8} placeholder="At least 8 characters" data-testid="reset-password-input" /></label>
          <label>Confirm password<input type="password" name="confirm" required minLength={8} placeholder="Repeat password" data-testid="reset-confirm-input" /></label>
          {error && <p className="admin-error" data-testid="reset-error">{error}</p>}
          <button className="primary-button submit-button" disabled={loading} data-testid="reset-submit-button">{loading ? "Updating..." : "Update password"}</button>
        </form>
      )}
    </div>
  );
}

function LoginView({ onLogin }) {
  const [mode, setMode] = useState("login");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);

  const submitLogin = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    const data = Object.fromEntries(new FormData(event.currentTarget));
    try {
      const { data: result } = await axios.post(`${API}/auth/login`, data);
      localStorage.setItem("wara_admin_token", result.access_token);
      onLogin(result.access_token);
      toast.success("Signed in.");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const submitForgot = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    const data = Object.fromEntries(new FormData(event.currentTarget));
    try {
      await axios.post(`${API}/auth/forgot-password`, data);
      setNotice("If that email is registered, a reset link has been sent. It expires in 1 hour.");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="admin-card">
      <a href="/" className="admin-back" data-testid="admin-back-link"><ArrowLeft size={16} /> Back to website</a>
      {mode === "login" ? (
        <>
          <h1>Admin sign in</h1>
          <p>Private area for the WARA team to review quote enquiries.</p>
          <form onSubmit={submitLogin} data-testid="admin-login-form">
            <label>Email<input type="email" name="email" required placeholder="admin@waraconstruction.com" data-testid="admin-email-input" /></label>
            <label>Password<input type="password" name="password" required placeholder="••••••••" data-testid="admin-password-input" /></label>
            {error && <p className="admin-error" data-testid="admin-login-error">{error}</p>}
            <button className="primary-button submit-button" disabled={loading} data-testid="admin-login-submit">
              {loading ? "Signing in..." : "Sign in"} <ShieldCheck size={18} />
            </button>
          </form>
          <button className="link-button" onClick={() => { setMode("forgot"); setError(""); }} data-testid="forgot-password-link">Forgot password?</button>
        </>
      ) : (
        <>
          <h1>Reset password</h1>
          <p>Enter your admin email and we’ll send a secure reset link.</p>
          {notice ? (
            <p className="admin-notice" data-testid="forgot-success-message">{notice}</p>
          ) : (
            <form onSubmit={submitForgot} data-testid="forgot-password-form">
              <label>Email<input type="email" name="email" required placeholder="admin@waraconstruction.com" data-testid="forgot-email-input" /></label>
              {error && <p className="admin-error" data-testid="forgot-error">{error}</p>}
              <button className="primary-button submit-button" disabled={loading} data-testid="forgot-submit-button">{loading ? "Sending..." : "Send reset link"}</button>
            </form>
          )}
          <button className="link-button" onClick={() => { setMode("login"); setNotice(""); }} data-testid="forgot-back-to-login">Back to sign in</button>
        </>
      )}
    </div>
  );
}

export default function Admin() {
  const resetToken = new URLSearchParams(window.location.search).get("reset_token");
  const [token, setToken] = useState(() => localStorage.getItem("wara_admin_token") || "");
  const [quotes, setQuotes] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectForm, setProjectForm] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadQuotes = useCallback(async (activeToken) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/quotes`, authHeaders(activeToken));
      setQuotes(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("wara_admin_token");
        setToken("");
      } else {
        toast.error("Could not load enquiries.");
      }
    } finally { setLoading(false); }
  }, []);

  const loadProjects = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/projects`);
      setProjects(data);
    } catch (e) { toast.error("Could not load projects."); }
  }, []);

  useEffect(() => { if (token) { loadQuotes(token); loadProjects(); } }, [token, loadQuotes, loadProjects]);

  const signOut = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (e) { /* token dropped locally */ }
    localStorage.removeItem("wara_admin_token");
    setToken("");
    setQuotes(null);
  };

  const changeStatus = async (quote, status) => {
    try {
      await axios.patch(`${API}/quotes/${quote.id}/status`, { status }, authHeaders(token));
      setQuotes((current) => current.map((q) => (q.id === quote.id ? { ...q, status } : q)));
      toast.success(`Marked as ${status}.`);
    } catch (e) { toast.error("Could not update status."); }
  };

  const saveProject = async (event) => {
    event.preventDefault();
    const data = Object.fromEntries(new FormData(event.currentTarget));
    try {
      if (projectForm.id) {
        await axios.put(`${API}/projects/${projectForm.id}`, data, authHeaders(token));
        toast.success("Project updated.");
      } else {
        await axios.post(`${API}/projects`, data, authHeaders(token));
        toast.success("Project added to the website.");
      }
      setProjectForm(null);
      loadProjects();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const removeProject = async (project) => {
    if (!window.confirm(`Delete project "${project.name}" from the website?`)) return;
    try {
      await axios.delete(`${API}/projects/${project.id}`, authHeaders(token));
      toast.success("Project deleted.");
      loadProjects();
    } catch (e) { toast.error("Could not delete project."); }
  };

  let content;
  if (resetToken) {
    content = <ResetPasswordView resetToken={resetToken} />;
  } else if (!token) {
    content = <LoginView onLogin={setToken} />;
  } else {
    content = (
      <div className="admin-dash">
        <a href="/" className="admin-back" data-testid="admin-back-link"><ArrowLeft size={16} /> Back to website</a>
        <div className="admin-dash-top">
          <h1 data-testid="admin-dashboard-title">Quote enquiries{quotes ? ` (${quotes.length})` : ""}</h1>
          <div className="admin-dash-actions">
            <button className="secondary-button" onClick={() => { loadQuotes(token); loadProjects(); }} disabled={loading} data-testid="admin-refresh-button">
              <RefreshCw size={16} /> Refresh
            </button>
            <button className="secondary-button" onClick={signOut} data-testid="admin-logout-button">
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>
        {quotes === null ? (
          <div className="admin-empty" data-testid="admin-loading">Loading enquiries...</div>
        ) : quotes.length === 0 ? (
          <div className="admin-empty" data-testid="admin-empty-state">No quote enquiries yet. New website submissions will appear here.</div>
        ) : (
          <div data-testid="admin-quote-list">
            {quotes.map((quote, index) => (
              <article className="quote-item" key={quote.id || index} data-testid={`admin-quote-item-${index}`}>
                <div className="quote-item-head">
                  <strong>{quote.name} — {quote.company}</strong>
                  <span>{quote.created_at ? new Date(quote.created_at).toLocaleString() : ""}</span>
                </div>
                <div className="quote-meta">
                  <span>Email: <b>{quote.email}</b></span>
                  <span>Phone: <b>{quote.phone}</b></span>
                  <span>Service: <b>{quote.service}</b></span>
                  <label className="status-field">Status:
                    <select value={quote.status || "New"} onChange={(e) => changeStatus(quote, e.target.value)} className={`status-select status-${(quote.status || "New").toLowerCase()}`} data-testid={`quote-status-select-${index}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </label>
                </div>
                <p><b>Scope:</b> {quote.project_scope}</p>
                {quote.message && <p><b>Message:</b> {quote.message}</p>}
              </article>
            ))}
          </div>
        )}

        <div className="admin-dash-top projects-top">
          <h1 data-testid="admin-projects-title">Website projects{projects ? ` (${projects.length})` : ""}</h1>
          <div className="admin-dash-actions">
            <button className="secondary-button" onClick={() => setProjectForm({ ...emptyProject })} data-testid="project-add-button">
              <Plus size={16} /> Add project
            </button>
          </div>
        </div>
        <p className="admin-hint">Projects added here replace the placeholder cards in the Projects section of the website.</p>

        {projectForm && (
          <form className="project-form" onSubmit={saveProject} data-testid="project-form">
            <h2>{projectForm.id ? "Edit project" : "Add project"}</h2>
            <div className="form-row">
              <label>Project name<input name="name" required defaultValue={projectForm.name} placeholder="Solar park civil works" data-testid="project-name-input" /></label>
              <label>Client name<input name="client" defaultValue={projectForm.client} placeholder="Adani Green Energy Ltd." data-testid="project-client-input" /></label>
            </div>
            <div className="form-row">
              <label>Location<input name="location" defaultValue={projectForm.location} placeholder="Barmer, Rajasthan" data-testid="project-location-input" /></label>
              <label>Year<input name="year" defaultValue={projectForm.year} placeholder="2025" data-testid="project-year-input" /></label>
            </div>
            <label>Project description<textarea name="description" defaultValue={projectForm.description} placeholder="Scope of work, outcome..." data-testid="project-description-input" /></label>
            <label>Project photo URL<input name="photo" type="url" defaultValue={projectForm.photo} placeholder="https://..." data-testid="project-photo-input" /></label>
            <div className="admin-dash-actions">
              <button className="primary-button" type="submit" data-testid="project-save-button">{projectForm.id ? "Save changes" : "Add project"}</button>
              <button className="secondary-button" type="button" onClick={() => setProjectForm(null)} data-testid="project-cancel-button">Cancel</button>
            </div>
          </form>
        )}

        {projects.length === 0 ? (
          <div className="admin-empty" data-testid="projects-empty-state">No projects added yet. The website is showing the default placeholder cards.</div>
        ) : (
          <div data-testid="admin-project-list">
            {projects.map((project, index) => (
              <article className="quote-item project-item" key={project.id} data-testid={`admin-project-item-${index}`}>
                {project.photo && <img className="project-thumb" src={project.photo} alt={project.name} />}
                <div className="project-item-body">
                  <div className="quote-item-head">
                    <strong>{project.name}</strong>
                    <span>{[project.client, project.location, project.year].filter(Boolean).join(" · ")}</span>
                  </div>
                  {project.description && <p>{project.description}</p>}
                  <div className="admin-dash-actions">
                    <button className="secondary-button" onClick={() => setProjectForm({ ...project })} data-testid={`project-edit-button-${index}`}><Pencil size={16} /> Edit</button>
                    <button className="secondary-button danger-button" onClick={() => removeProject(project)} data-testid={`project-delete-button-${index}`}><Trash2 size={16} /> Delete</button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="admin-shell" data-testid={token ? "admin-dashboard" : "admin-login-page"}>
      {content}
      <Toaster position="bottom-right" />
    </div>
  );
}
